<template>
  <v-container>
    <v-layout>
      <v-flex xs12>
        <h1 class="display-1 mt-10">Position Converter</h1>
        <p>
          Converts reference positions to selector orientated positions and vice
          versa.
        </p>
        <v-sheet elevation="2" class="mt-10 pa-10">
          <v-form ref="form" v-model="valid" :lazy-validation="lazy">
            <v-row>
              <v-subheader>From</v-subheader>
            </v-row>
            <v-divider></v-divider>
            <v-row class="pl-2 pr-2">
              <v-col cols="12" sm="6" lg="3">
                <v-text-field
                  ref="referenceId"
                  v-model="referenceId"
                  :rules="rules"
                  :label="'Reference ID'"
                  :hint="'E.g. NC_000001.11'"
                  :clearable="true"
                  :error-messages="errorReferenceIdMessage"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-combobox
                  ref="fromSelectorId"
                  v-model="fromSelectorId"
                  :label="'Selector ID'"
                  :hint="'E.g. NM_001232.3'"
                  :items="availableSelectors.selectors"
                  :click="getAvailableSelectors()"
                  :clearable="true"
                  :error-messages="errorFromSelectorIdMessage"
                ></v-combobox>
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-select
                  v-model="fromCoordinateSystem"
                  :items="['g', 'c', 'n']"
                  label="Coordinate system"
                  :clearable="true"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-text-field
                  ref="position"
                  v-model="position"
                  :rules="rules"
                  :label="'Position'"
                  :hint="'E.g. 100'"
                  :error-messages="errorPositionMessage"
                  :clearable="true"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-subheader>To</v-subheader>
            </v-row>
            <v-divider></v-divider>
            <v-row class="pl-2 pr-2">
              <v-col cols="12" sm="6" lg="3">
                <v-combobox
                  v-model="toSelectorId"
                  :label="'Selector ID'"
                  :hint="'E.g. NM_001232.3'"
                  :items="availableSelectors.selectors"
                  :click="getAvailableSelectors()"
                  :clearable="true"
                  :error-messages="errorToSelectorIdMessage"
                ></v-combobox>
              </v-col>

              <v-col cols="12" sm="6" lg="3">
                <v-select
                  v-model="toCoordinateSystem"
                  :items="['g', 'c', 'n']"
                  :clearable="true"
                  label="Coordinate system"
                ></v-select>
              </v-col>
            </v-row>
            <v-row class="mt--10">
              <v-col>
                <v-switch
                  v-model="includeOverlapping"
                  label="Include overlapping selectors"
                  color="primary"
                ></v-switch>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-menu transition="slide-x-transition">
                  <template #activator="{ on, attrs }">
                    <span
                      class="example-link"
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                    >
                      Examples
                    </span>
                  </template>
                  <v-list>
                    <v-list-item v-for="(example, i) in examples" :key="i" link>
                      <v-list-item-title
                        color="success"
                        @click.prevent="setExample(example.fields)"
                        >{{ example.item }}</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
          <v-row class="pl-3">
            <v-btn
              ref="convert"
              class="mt-5"
              color="primary"
              :disabled="!valid"
              :to="{
                name: 'PositionConverter',
                query: {
                  referenceId: referenceId,
                  fromSelectorId: fromSelectorId,
                  fromCoordinateSystem: fromCoordinateSystem,
                  position: position,
                  toSelectorId: toSelectorId,
                  toCoordinateSystem: toCoordinateSystem,
                  includeOverlapping: includeOverlapping,
                },
              }"
            >
              Convert
            </v-btn>
          </v-row>
        </v-sheet>

        <v-overlay :absolute="true" :value="loadingOverlay">
          <div class="text-center">
            <v-progress-circular :size="50" indeterminate></v-progress-circular>
          </div>
          <div class="text-center">
            <v-btn class="mt-5" @click="loadingOverlay = false"> Cancel </v-btn>
          </div>
        </v-overlay>

        <v-alert
          v-if="isConverted()"
          ref="successAlert"
          class="mt-10 mb-0"
          elevation="2"
          prominent
          tile
          type="success"
        >
          <v-row align="center">
            <v-col class="grow">
              <span style="font-family: monospace">{{
                converted_description
              }}</span>
            </v-col>
            <v-col class="shrink">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-clipboard="converted_description"
                    v-bind="attrs"
                    icon
                    v-on="on"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>Copy</span>
              </v-tooltip>
            </v-col>
            <v-col v-if="correctionsPerformed()" class="shrink">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    icon
                    v-on="on"
                    @click="showCorrections = !showCorrections"
                  >
                    <v-icon>
                      mdi-details {{ showCorrections ? "mdi-rotate-180" : "" }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ showCorrections ? "Hide" : "See" }} Details</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-alert>

        <v-alert
          v-if="connectionErrors"
          prominent
          type="error"
          tile
          elevation="2"
          class="mt-10"
          icon="mdi-network-off-outline"
          color="grey darken-4"
        >
          <v-row align="center">
            <v-col class="grow">
              {{ connectionErrors.details }}
            </v-col>
          </v-row>
        </v-alert>

        <v-alert
          v-if="errorsEncountered()"
          prominent
          type="error"
          tile
          elevation="2"
          class="mt-10 mb-0"
        >
          <v-row align="center">
            <v-col class="grow overline"
              >Conversion could not be performed</v-col
            >
            <v-col v-if="correctionsPerformed()" class="shrink">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    icon
                    v-on="on"
                    @click="showCorrections = !showCorrections"
                  >
                    <v-icon>
                      mdi-details {{ showCorrections ? "mdi-rotate-180" : "" }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ showCorrections ? "Hide" : "See" }} Details</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-alert>

        <v-expand-transition>
          <v-sheet
            v-if="
              (correctionsPerformed() && showCorrections) || errorsEncountered()
            "
            elevation="2"
          >
            <v-expand-transition>
              <v-sheet
                v-if="correctionsPerformed() && showCorrections"
                ref="refCorrections"
                class="pt-5 pr-10 pb-5 pl-10"
                color="grey lighten-5"
              >
                <div class="overline">Corrections</div>
                <v-alert
                  v-for="(info, index) in response.infos"
                  :key="index"
                  color="light-blue lighten-5"
                  tile
                  border="left"
                  class="ml-2"
                >
                  {{ getMessage(info) }}
                </v-alert>
              </v-sheet>
            </v-expand-transition>
            <v-sheet
              v-if="errorsEncountered()"
              class="pt-10 pr-10 pb-8 pl-10"
              color="red lighten-5"
            >
              <v-alert
                v-for="(error, index) in errorMessages"
                :key="index"
                color="red lighten-1"
                tile
                border="left"
                dark
              >
                <div v-if="error.code == 'EPOSITIONSYNTAX'">
                  <SyntaxError :error-model="error" />
                </div>
                <div v-else>
                  {{ getMessage(error) }}
                </div>
              </v-alert>
            </v-sheet>
          </v-sheet>
        </v-expand-transition>

        <v-expansion-panels
          v-if="response && response.overlap"
          focusable
          hover
          class="mt-5 mb-5"
          tile
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="overline"
              >Overlapping selectors</v-expansion-panel-header
            >
            <v-expansion-panel-content class="pt-5">
              <div v-for="(values, c_s) in response.overlap" :key="c_s">
                <v-subheader v-if="c_s == 'c'" class="overline"
                  >Coding</v-subheader
                >
                <v-subheader v-else-if="c_s == 'n'" class="overline"
                  >Noncoding</v-subheader
                >
                <v-subheader v-else-if="c_s == 'g'" class="overline"
                  >Genomic</v-subheader
                >
                <v-subheader v-else class="overline"> {{ c_s }} </v-subheader>
                <v-sheet v-for="(overlap_model, index) in values" :key="index">
                  <v-hover v-slot="{ hover }">
                    <v-sheet
                      :color="hover ? 'grey lighten-3' : ''"
                      class="pa-2 ma-1"
                    >
                      <Description
                        :description="
                          modelToDescription(overlap_model['description'])
                        "
                        :css_class="'ok-description'"
                      />
                    </v-sheet>
                  </v-hover>
                  <v-divider v-if="index != values.length - 1"></v-divider>
                </v-sheet>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-expansion-panels v-if="response" focusable hover class="mt-10 mb-10">
          <v-expansion-panel>
            <v-expansion-panel-header>Raw Response</v-expansion-panel-header>
            <v-expansion-panel-content>
              <JsonPretty :summary="response" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import DescriptionModel from "../mixins/DescriptionModel.js";
import MutalyzerService from "../services/MutalyzerService.js";
import JsonPretty from "../components/JsonPretty.vue";
import SyntaxError from "../components/SyntaxError.vue";
import Description from "../components/Description.vue";

export default {
  components: {
    JsonPretty,
    SyntaxError,
    Description,
  },
  mixins: [DescriptionModel],
  data: () => ({
    valid: true,
    lazy: false,

    referenceId: "",
    fromSelectorId: "",
    fromCoordinateSystem: "",
    position: "",
    toSelectorId: "",
    toCoordinateSystem: "",
    includeOverlapping: false,

    rules: [(value) => !!value || "Required."],
    loadingOverlay: false,

    response: null,
    converted_description: null,
    errorMessages: null,
    errorReferenceId: null,
    errorReferenceIdMessage: null,
    errorFromSelectorId: null,
    errorFromSelectorIdMessage: null,
    errorToSelectorId: null,
    errorToSelectorIdMessage: null,
    errorPosition: null,
    errorPositionMessage: null,
    referenceErrors: null,
    otherSelectors: null,
    availableSelectors: {},

    showCorrections: false,
    connectionErrors: null,

    examples: [
      {
        item: "LRG_24:g.100 -> t1",
        fields: {
          referenceId: "LRG_24",
          position: "100",
          toSelectorId: "t1",
        },
      },
      {
        item: "NG_007485.1(NR_047542.1):n.274 -> g.",
        fields: {
          referenceId: "NG_007485.1",
          fromSelectorId: "NR_047542.1",
          position: "274",
        },
      },
      {
        item: "NG_017013.2(NM_000546.5):c.274 -> NM_000546.5",
        fields: {
          referenceId: "NG_017013.2",
          fromSelectorId: "NM_000546.5",
          position: "274",
        },
      },
      {
        item: "NG_012337.1:g.274 -> NG_012337.1(NM_003002.2)",
        fields: {
          referenceId: "NG_012337.1",
          position: "274",
          toSelectorId: "NM_003002.2",
        },
      },
      {
        item: "NC_000001.11(NM_001232.4):c.274 -> NC_000001.11",
        fields: {
          referenceId: "NC_000001.11",
          fromSelectorId: "NM_001232.4",
          position: "274",
        },
      },
    ],
  }),
  watch: {
    $route() {
      this.run();
    },
    referenceId() {
      if (this.availableSelectors.reference !== this.referenceId) {
        this.availableSelectors = {};
      }
      this.handleEretr();
    },
    fromSelectorId() {
      this.handleENoFromSelector();
    },
    toSelectorId() {
      this.handleENoToSelector();
    },
    position() {
      this.updatePositionErrorMessage();
    },
  },
  created: function () {
    this.run();
  },
  methods: {
    run: function () {
      if (
        this.$route.query.referenceId &&
        0 !== this.$route.query.referenceId.length
      ) {
        this.referenceId = this.$route.query.referenceId;
      }
      if (
        this.$route.query.fromSelectorId &&
        0 !== this.$route.query.fromSelectorId.length
      ) {
        this.fromSelectorId = this.$route.query.fromSelectorId;
      }
      if (
        this.$route.query.fromCoordinateSystem &&
        0 !== this.$route.query.fromCoordinateSystem.length
      ) {
        this.fromCoordinateSystem = this.$route.query.fromCoordinateSystem;
      }
      if (
        this.$route.query.position &&
        0 !== this.$route.query.position.length
      ) {
        this.position = this.$route.query.position;
      }
      if (
        this.$route.query.toSelectorId &&
        0 !== this.$route.query.toSelectorId.length
      ) {
        this.toSelectorId = this.$route.query.toSelectorId;
      }
      if (
        this.$route.query.toCoordinateSystem &&
        0 !== this.$route.query.toCoordinateSystem.length
      ) {
        this.toCoordinateSystem = this.$route.query.toCoordinateSystem;
      }
      if (this.$route.query.includeOverlapping) {
        if (this.$route.query.includeOverlapping === "true") {
          this.includeOverlapping = true;
        } else if (this.$route.query.includeOverlapping === "false") {
          this.includeOverlapping = false;
        }
      }
      if (
        this.$route.query.referenceId &&
        0 !== this.$route.query.referenceId.length &&
        this.$route.query.position &&
        0 !== this.$route.query.position.length
      ) {
        this.positionConvert();
      }
    },
    setExample: function (fields) {
      if (fields.referenceId) {
        this.referenceId = fields.referenceId;
      } else {
        this.referenceId = "";
      }
      if (fields.fromSelectorId) {
        this.fromSelectorId = fields.fromSelectorId;
      } else {
        this.fromSelectorId = "";
      }
      if (fields.fromCoordinateSystem) {
        this.fromCoordinateSystem = fields.fromCoordinateSystem;
      } else {
        this.fromCoordinateSystem = "";
      }
      if (fields.position) {
        this.position = fields.position;
      } else {
        this.position = "";
      }
      if (fields.toSelectorId) {
        this.toSelectorId = fields.toSelectorId;
      } else {
        this.toSelectorId = "";
      }
      if (fields.toCoordinateSystem) {
        this.toCoordinateSystem = fields.toCoordinateSystem;
      } else {
        this.toCoordinateSystem = "";
      }
    },
    positionConvert: function () {
      this.errorMessages = null;
      if (this.referenceId !== null && this.position !== null) {
        this.loadingOverlay = true;
        this.response = null;
        this.converted_description = null;
        this.showCorrections = false;
        this.connectionErrors = null;

        MutalyzerService.positionConvert(this.getParams())
          .then((response) => {
            if (response.data) {
              this.loadingOverlay = false;
              this.response = response.data;

              if (this.isConverted()) {
                this.converted_description = this.modelToDescription(
                  this.response.converted_model,
                );
                this.$nextTick(() => {
                  this.$vuetify.goTo(this.$refs.successAlert, this.options);
                });
              }
            }
          })
          .catch((error) => {
            this.loadingOverlay = false;
            if (error.response) {
              if (
                error.response.status == 422 &&
                error.response.data &&
                error.response.data.custom &&
                error.response.data.custom.errors
              ) {
                this.errorsHandler(error.response.data.custom.errors);
              } else {
                this.connectionErrors = {
                  details: "Some response error occured.",
                };
              }
            } else if (error.request) {
              this.connectionErrors = {
                details: "Some connection or server error occured.",
              };
            } else {
              this.connectionErrors = { details: "Some error occured." };
            }
          });
      }
    },
    isConverted: function () {
      if (this.response && this.response.converted_model) {
        return true;
      } else {
        return false;
      }
    },
    correctionsPerformed: function () {
      return this.response && this.response.infos;
    },
    errorsEncountered: function () {
      if (this.errorMessages) {
        return true;
      } else {
        return false;
      }
    },
    getMessage: function (message) {
      if (message.details) {
        return message.details + " (" + message.code + ")";
      }
      return message;
    },
    errorsHandler: function (errors) {
      this.errorMessages = errors;
      for (const entry of errors) {
        if (entry.code === "ERETR") {
          this.errorReferenceId = this.referenceId;
          this.handleEretr();
        } else if (entry.code === "ENOSELECTORFOUND") {
          this.errorFromSelectorId = this.fromSelectorId;
          this.handleENoFromSelector();
        } else if (entry.code === "ENOTOSELECTOR") {
          this.errorToSelectorId = this.toSelectorId;
          this.handleENoToSelector();
        } else if (entry.code === "EPOSITIONSYNTAX") {
          this.errorPosition = this.position;
          this.updatePositionErrorMessage("Syntax error.");
        } else if (entry.code === "ERANGELOCATION") {
          this.errorPosition = this.position;
          this.updatePositionErrorMessage("Range locations not supported.");
        } else if (entry.code === "EOUTOFBOUNDARY") {
          this.errorPosition = this.position;
          this.updatePositionErrorMessage(
            "Position out of sequence boundaries.",
          );
        }
      }
    },
    handleEretr() {
      if (this.errorReferenceId && this.errorReferenceId === this.referenceId) {
        this.errorReferenceIdMessage = this.referenceId + " not retrieved";
      } else {
        this.errorReferenceIdMessage = null;
      }
    },
    handleENoFromSelector() {
      if (
        this.errorFromSelectorId &&
        this.errorFromSelectorId === this.fromSelectorId
      ) {
        this.errorFromSelectorIdMessage =
          this.fromSelectorId + " not found in " + this.referenceId;
      } else {
        this.errorFromSelectorIdMessage = null;
      }
    },
    handleENoToSelector() {
      if (
        this.errorToSelectorId &&
        this.errorToSelectorId === this.toSelectorId
      ) {
        this.errorToSelectorIdMessage =
          this.toSelectorId + " not in reference " + this.referenceId;
      } else {
        this.errorToSelectorIdMessage = null;
      }
    },
    updatePositionErrorMessage(message) {
      if (this.errorPosition && this.errorPosition === this.position) {
        this.errorPositionMessage = message;
      } else {
        this.errorPositionMessage = null;
      }
    },
    getAvailableSelectors: function () {
      if (this.referenceId !== null && 0 !== this.referenceId.length) {
        if (
          this.availableSelectors &&
          this.availableSelectors.reference !== this.referenceId
        ) {
          MutalyzerService.getSelectors(this.referenceId)
            .then((response) => {
              if (response.data) {
                this.availableSelectors = response.data;
              }
            })
            .catch((error) => {
              this.availableSelectors = {
                reference: this.referenceId,
                error: error,
              };
            });
        }
      }
    },
    getDescription: function (reference_id, position) {
      let output = reference_id;
      if (position.selector_id) {
        output += "(" + position.selector_id + ")";
      }
      output += ":" + position.coordinate_system + ".";
      output += position.position;
      return output;
    },
    getParams: function () {
      let params = {
        reference_id: this.referenceId,
        position: this.position,
        include_overlapping: this.includeOverlapping,
      };
      if (this.fromSelectorId) {
        params["from_selector_id"] = this.fromSelectorId;
      }
      if (this.fromCoordinateSystem) {
        params["from_coordinate_system"] = this.fromCoordinateSystem;
      }
      if (this.toSelectorId) {
        params["to_selector_id"] = this.toSelectorId;
      }
      if (this.toCoordinateSystem) {
        params["to_coordinate_system"] = this.toCoordinateSystem;
      }

      return params;
    },
  },
};
</script>

<style scoped src="../assets/main.css"></style>
